import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/common/StatusBadge";
import { Building2, Plus, Search, ExternalLink, Filter } from "lucide-react";
import { formatSiren } from "@/lib/format";
import { toast } from "sonner";
import {
  LEGAL_FORM_LABELS, STATUS_LABELS, MANAGEMENT_MODE_LABELS, getLegalFormTone,
} from "@/constants/group";

const EMPTY_FORM = {
  denomination: "", legal_form: "SCI_IS", tax_regime: "IS",
  siren: "", management_mode: "full", parent_company_id: "",
  activity: "", city: "", postal_code: "", address: "",
};

export default function EntitiesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [filters, setFilters] = useState({
    search: "", legal_form: "any", tax_regime: "any",
    management_mode: "any", status: "any",
  });

  const query = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "any") p.set(k, v); });
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/companies${query}`);
      setItems(data.items || []);
    } catch {} setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [query]);

  const create = async () => {
    try {
      const payload = { ...form };
      if (!payload.parent_company_id) delete payload.parent_company_id;
      const { data } = await api.post("/companies", payload);
      toast.success("Entité créée");
      setOpen(false); setForm({ ...EMPTY_FORM });
      load();
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de créer l'entité");
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters row */}
      <div className="card-elevated p-4 lg:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Nom, SIREN, activité, dirigeant…"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9"
                data-testid="entities-filter-search"
              />
            </div>
          </div>
          <FilterSelect label="Type" value={filters.legal_form} onValueChange={(v) => setFilters({ ...filters, legal_form: v })}
                        options={[["any", "Tous les types"], ...Object.entries(LEGAL_FORM_LABELS)]} testId="entities-filter-type" />
          <FilterSelect label="Régime fiscal" value={filters.tax_regime} onValueChange={(v) => setFilters({ ...filters, tax_regime: v })}
                        options={[["any", "Tous régimes"], ["IS", "IS"], ["IR", "IR"], ["MICRO", "Micro"], ["EXO", "Exo"], ["N/A", "N/A"]]}
                        testId="entities-filter-tax" />
          <FilterSelect label="Mode" value={filters.management_mode} onValueChange={(v) => setFilters({ ...filters, management_mode: v })}
                        options={[["any", "Tous modes"], ...Object.entries(MANAGEMENT_MODE_LABELS)]}
                        testId="entities-filter-mode" />
          <FilterSelect label="Statut" value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}
                        options={[["any", "Tous statuts"], ...Object.entries(STATUS_LABELS)]}
                        testId="entities-filter-status" />
        </div>
      </div>

      {/* Header actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          <Filter className="inline h-3.5 w-3.5 mr-1" /> {items.length} entité{items.length > 1 ? "s" : ""}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="entity-add-btn">
              <Plus className="mr-2 h-4 w-4" /> Nouvelle entité
            </Button>
          </DialogTrigger>
          <CreateEntityDialog form={form} setForm={setForm} onCreate={create} onCancel={() => setOpen(false)} companies={items} />
        </Dialog>
      </div>

      {/* Table */}
      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-4">Entité</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4">Fiscal</th>
              <th className="px-6 py-4">SIREN</th>
              <th className="px-6 py-4">Mode</th>
              <th className="px-6 py-4">Statut</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-16 text-center text-muted-foreground">
                <Building2 className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" />
                Aucune entité ne correspond aux filtres.
              </td></tr>
            )}
            {items.map((c) => {
              const tone = getLegalFormTone(c.legal_form);
              return (
                <tr key={c.id} data-testid={`entity-row-${c.id}`} className="hover:bg-secondary/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{c.denomination || c.name}</div>
                    {c.trade_name && <div className="text-xs text-muted-foreground">{c.trade_name}</div>}
                    {c.activity && <div className="text-xs text-muted-foreground italic">{c.activity}</div>}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${tone.bg} ${tone.text} ${tone.border}`}>
                      {LEGAL_FORM_LABELS[c.legal_form] || c.legal_form}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground text-xs">{c.tax_regime || "—"}{c.vat_applicable ? " · TVA" : ""}</td>
                  <td className="px-6 py-4 font-mono text-xs">{formatSiren(c.siren)}</td>
                  <td className="px-6 py-4">
                    <StatusBadge tone={c.management_mode === "reference" ? "warning" : "success"}>
                      {MANAGEMENT_MODE_LABELS[c.management_mode || "full"]}
                    </StatusBadge>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge tone={c.status === "active" ? "success" : c.status === "archived" ? "neutral" : "warning"}>
                      {STATUS_LABELS[c.status || "active"]}
                    </StatusBadge>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button asChild variant="ghost" size="sm" data-testid={`entity-open-${c.id}`}>
                      <Link to={`/app/companies/${c.id}`}><ExternalLink className="h-4 w-4" /></Link>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onValueChange, options, testId }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="mt-1" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function CreateEntityDialog({ form, setForm, onCreate, onCancel, companies }) {
  return (
    <DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle>Nouvelle entité</DialogTitle></DialogHeader>
      <div className="space-y-4 py-2">
        <div>
          <Label>Dénomination</Label>
          <Input value={form.denomination} onChange={(e) => setForm({ ...form, denomination: e.target.value })}
                 data-testid="entity-form-denomination" required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Forme juridique</Label>
            <Select value={form.legal_form} onValueChange={(v) => setForm({ ...form, legal_form: v })}>
              <SelectTrigger data-testid="entity-form-legal-form"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(LEGAL_FORM_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Régime fiscal</Label>
            <Select value={form.tax_regime} onValueChange={(v) => setForm({ ...form, tax_regime: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="IS">IS</SelectItem><SelectItem value="IR">IR</SelectItem>
                <SelectItem value="MICRO">Micro</SelectItem><SelectItem value="EXO">Exonéré</SelectItem>
                <SelectItem value="N/A">Non applicable</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>SIREN</Label>
            <Input value={form.siren} onChange={(e) => setForm({ ...form, siren: e.target.value })} placeholder="123 456 789" />
          </div>
          <div>
            <Label>Mode de gestion</Label>
            <Select value={form.management_mode} onValueChange={(v) => setForm({ ...form, management_mode: v })}>
              <SelectTrigger data-testid="entity-form-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MANAGEMENT_MODE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Activité</Label>
          <Input value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} />
        </div>
        <div>
          <Label>Société mère (optionnel)</Label>
          <Select value={form.parent_company_id || "none"} onValueChange={(v) => setForm({ ...form, parent_company_id: v === "none" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Aucune</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denomination || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-3">
          <div><Label>Code postal</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Annuler</Button>
        <Button onClick={onCreate} data-testid="entity-form-submit">Créer l'entité</Button>
      </DialogFooter>
    </DialogContent>
  );
}
